'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useGetServiceTypesQuery } from '@/features/service-types/api/service-typesApi';
import { ServiceTypeSelect } from '@/features/service-types/components';
import {
  useCreateTaskMutation,
  useDeleteTaskMutation,
  useGetTasksQuery,
  useUpdateTaskMutation,
} from '@/features/tasks/api/tasksApi';
import {
  AssigneeSelect,
  WorkspaceMemberAvatar,
} from '@/features/workspaces/components';
import { useCanWorkspaceAction } from '@/features/workspaces/hooks';
import {
  formatDueDateLabel,
  getDueDateRange,
  getTimelineStatusLabel,
} from '@/utils/tasks';
import {
  Badge,
  DatePicker,
  EmptyState,
  InfiniteTable,
  Meter,
  OptionsMenu,
  SmartInput,
} from '@web/ui';
import {
  backendErrorsToFieldErrors,
  formatHours,
  getErrorMessage,
  notify,
  truncateWithEllipsis,
} from '@web/utils';

import { EditableTaskTitle } from './EditableTaskTitle';
import { QuickTaskInlineCreateForm } from './QuickTaskInlineCreateForm';
import styles from './QuickTasksTable.module.scss';
import { QuickTaskSubRowForm } from './QuickTaskSubRowForm';
import { TaskStatusSelect } from './TaskStatusSelect';
import { UpdateTaskDueDate } from './UpdateTaskDueDate';
import { UpdateTaskStatus } from './UpdateTaskStatus';

import type { CompanyOption } from '@/features/contacts/companies/components/CompanySelectField';
import type { PersonOption } from '@/features/contacts/people/components/PersonSelectField';
import type { SelectTaskStatusValue } from '@/features/tasks/components';
import type {
  CreateTaskPayload,
  Task,
  TaskStatus,
  UpdateTaskPayload,
} from '@/features/tasks/types';
import type { TaskDueDateFilter } from '@/utils/tasks';
import type { Dayjs } from 'dayjs';

type QuickTaskInlineDraftRow = {
  id: string;
  __kind: 'quick-task-inline-draft';
};

type QuickTaskRow = Task | QuickTaskInlineDraftRow;

type ClientMode = 'client' | 'internal';

type InlineCreateFormState = {
  title: string;
  description: string;
  clientMode: ClientMode;
  company: CompanyOption | null;
  person: PersonOption | null;
  status: TaskStatus;
  assigneeId: string;
  serviceTypeId: string;
  dueDate: Dayjs | null;
  levelOfEffort: number | null;
};

type InlineCreateErrors = Partial<
  Record<
    | 'title'
    | 'description'
    | 'company_id'
    | 'person_id'
    | 'assignee_id'
    | 'service_type_id'
    | 'due_date'
    | 'level_of_effort'
    | 'form',
    string
  >
>;

type QuickTasksTableProps = {
  search: string;
  status: SelectTaskStatusValue;
  assigneeId: string;
  dueDateFilter: TaskDueDateFilter;
  isCreateTaskOpen: boolean;
  onCloseCreateTask: () => void;
};

const INLINE_CREATE_ROW_ID = '__quick-task-inline-create-row__';
const INLINE_CREATE_ROW: QuickTaskInlineDraftRow = {
  id: INLINE_CREATE_ROW_ID,
  __kind: 'quick-task-inline-draft',
};

function findScrollableAncestor(
  element: HTMLElement | null,
): HTMLElement | null {
  let current = element?.parentElement ?? null;

  while (current) {
    const style = window.getComputedStyle(current);
    const overflowY = style.overflowY;
    const canScrollY =
      (overflowY === 'auto' ||
        overflowY === 'scroll' ||
        overflowY === 'overlay') &&
      current.scrollHeight > current.clientHeight + 1;

    if (canScrollY) {
      return current;
    }

    current = current.parentElement;
  }

  return null;
}

function findScrollableDescendants(root: HTMLElement): HTMLElement[] {
  const nodes = Array.from(root.querySelectorAll<HTMLElement>('*'));

  return nodes.filter((node) => {
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY;
    const canScrollY =
      (overflowY === 'auto' ||
        overflowY === 'scroll' ||
        overflowY === 'overlay') &&
      node.scrollHeight > node.clientHeight + 1;

    return canScrollY;
  });
}

function isInlineCreateRow(row: QuickTaskRow): row is QuickTaskInlineDraftRow {
  return '__kind' in row;
}

function getPrimaryAssignee(task: Task) {
  return task.assignees?.[0] ?? null;
}

function getAssigneeServiceTypeId(task: Task): string {
  const primaryAssignee = getPrimaryAssignee(task);
  return (
    primaryAssignee?.service_type_id ?? primaryAssignee?.service_type?.id ?? ''
  );
}

function getAssigneeName(task: Task): string {
  const assignee = getPrimaryAssignee(task)?.user;

  if (!assignee) {
    return 'Unassigned';
  }

  const fullName =
    `${assignee.first_name ?? ''} ${assignee.last_name ?? ''}`.trim();
  return fullName || assignee.email || 'Unknown user';
}

function getLoePercentage(task: Task) {
  const assignee = getPrimaryAssignee(task);
  return assignee?.actual_time_tracked_percentage ?? 0;
}

function getInitialInlineCreateForm(): InlineCreateFormState {
  return {
    title: '',
    description: '',
    clientMode: 'client',
    company: null,
    person: null,
    status: 'inprogress',
    assigneeId: '',
    serviceTypeId: '',
    dueDate: null,
    levelOfEffort: 0,
  };
}

function toIsoDateString(rawValue: Dayjs | null): string | null {
  if (!rawValue || !rawValue.isValid()) {
    return null;
  }

  return rawValue.startOf('day').toISOString();
}

function validateInlineCreateForm(
  values: InlineCreateFormState,
): InlineCreateErrors {
  const nextErrors: InlineCreateErrors = {};

  if (!values.title.trim()) {
    nextErrors.title = 'Title is required.';
  }

  if (!values.assigneeId) {
    nextErrors.assignee_id = 'Assignee is required.';
  }

  if (!values.serviceTypeId) {
    nextErrors.service_type_id = 'Service type is required.';
  }

  if (values.levelOfEffort === null || values.levelOfEffort < 0) {
    nextErrors.level_of_effort = 'LOE (hours) is required.';
  }

  if (!values.dueDate) {
    nextErrors.due_date = 'Due date is required.';
  } else if (!values.dueDate.isValid()) {
    nextErrors.due_date = 'Use a valid date (YYYY-MM-DD).';
  }

  return nextErrors;
}

function mapBackendTaskFieldPath(path: string): string | null {
  if (path === 'title.0' || path === 'title') return 'title';
  if (path === 'description.0' || path === 'description') return 'description';
  if (path === 'due_date.0' || path === 'due_date') return 'due_date';
  if (path === 'company_contact_id.0' || path === 'company_contact_id')
    return 'company_id';
  if (path === 'person_contact_id.0' || path === 'person_contact_id')
    return 'person_id';
  if (path === 'task_assignee_payloads.0.user_id') return 'assignee_id';
  if (path === 'task_assignee_payloads.0.service_type_id')
    return 'service_type_id';
  if (path === 'task_assignee_payloads.0.level_of_effort')
    return 'level_of_effort';
  if (path === 'non_field_errors.0') return 'form';

  return path;
}

export function QuickTasksTable({
  search,
  status,
  assigneeId,
  dueDateFilter,
  isCreateTaskOpen,
  onCloseCreateTask,
}: QuickTasksTableProps) {
  const canEditTasks = useCanWorkspaceAction('task_management', 'edit', true);
  const [updateTask] = useUpdateTaskMutation();
  const [createTask, { isLoading: isCreatingTask }] = useCreateTaskMutation();
  const [deleteTask, { isLoading: isDeletingTask }] = useDeleteTaskMutation();
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);
  const tableRootRef = useRef<HTMLDivElement | null>(null);
  const hasAutoScrolledToInlineCreateRef = useRef(false);
  const [inlineCreateForm, setInlineCreateForm] =
    useState<InlineCreateFormState>(getInitialInlineCreateForm);
  const [inlineCreateErrors, setInlineCreateErrors] =
    useState<InlineCreateErrors>({});

  const dueDateRange = useMemo(
    () => getDueDateRange(dueDateFilter),
    [dueDateFilter],
  );

  const { data, isLoading, refetch } = useGetTasksQuery({
    page: 1,
    page_size: 100,
    task_type: 'quick',
    query: search || undefined,
    status: status === 'all' ? undefined : status,
    exclude_status: status === 'all' ? 'archived' : undefined,
    assignee_id: assigneeId || undefined,
    due_after: dueDateRange.due_after,
    due_before: dueDateRange.due_before,
    sort_field: 'created_at',
    is_descending: true,
  });

  const taskRows = useMemo<Task[]>(() => data?.data?.results ?? [], [data]);
  const rows = useMemo<QuickTaskRow[]>(
    () => (isCreateTaskOpen ? [...taskRows, INLINE_CREATE_ROW] : taskRows),
    [isCreateTaskOpen, taskRows],
  );
  const { data: serviceTypesData } = useGetServiceTypesQuery({
    page_size: 200,
    show_archived: false,
  });

  const serviceTypeLabelMap = useMemo(() => {
    return new Map(
      (serviceTypesData?.data?.results ?? []).map((serviceType) => [
        serviceType.id,
        serviceType.label,
      ]),
    );
  }, [serviceTypesData]);

  useEffect(() => {
    if (!isCreateTaskOpen) {
      hasAutoScrolledToInlineCreateRef.current = false;
      const timeoutId = window.setTimeout(() => {
        setExpandedRowIds((current) =>
          current.filter((rowId) => rowId !== INLINE_CREATE_ROW_ID),
        );
      }, 0);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }

    const timeoutId = window.setTimeout(() => {
      setInlineCreateForm(getInitialInlineCreateForm());
      setInlineCreateErrors({});
      setExpandedRowIds((current) =>
        current.includes(INLINE_CREATE_ROW_ID)
          ? current
          : [...current, INLINE_CREATE_ROW_ID],
      );
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isCreateTaskOpen]);

  const clearInlineCreateError = useCallback(
    (field: keyof InlineCreateErrors) => {
      setInlineCreateErrors((currentErrors) => {
        if (!currentErrors[field]) {
          return currentErrors;
        }

        return {
          ...currentErrors,
          [field]: undefined,
        };
      });
    },
    [],
  );

  const scrollToInlineCreateForm = useCallback(() => {
    const root = tableRootRef.current;
    if (!root) {
      return;
    }

    const detailRow = root.querySelector(
      `tr[data-row-type="detail"][data-parent-row-id="${INLINE_CREATE_ROW_ID}"]`,
    ) as HTMLElement | null;
    if (detailRow) {
      const scroller = findScrollableAncestor(detailRow);
      if (scroller) {
        const top = detailRow.offsetTop - scroller.offsetTop - 12;
        scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      } else {
        detailRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const draftRow = root.querySelector(
      `tr[data-row-type="data"][data-row-id="${INLINE_CREATE_ROW_ID}"]`,
    ) as HTMLElement | null;
    if (draftRow) {
      const scroller = findScrollableAncestor(draftRow);
      if (scroller) {
        const top = draftRow.offsetTop - scroller.offsetTop - 12;
        scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      } else {
        draftRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const tableContainer = root.querySelector(
      '[class*="tableContainer"]',
    ) as HTMLElement | null;
    const virtuosoScroller = root.querySelector(
      '[data-virtuoso-scroller="true"]',
    ) as HTMLElement | null;

    if (virtuosoScroller) {
      virtuosoScroller.scrollTo({
        top: virtuosoScroller.scrollHeight,
        behavior: 'smooth',
      });
      return;
    }

    if (tableContainer) {
      tableContainer.scrollTo({
        top: tableContainer.scrollHeight,
        behavior: 'smooth',
      });
      return;
    }

    const scrollables = findScrollableDescendants(root);
    if (scrollables.length > 0) {
      const likelyScroller = scrollables.reduce((best, current) =>
        current.scrollHeight > best.scrollHeight ? current : best,
      );

      likelyScroller.scrollTo({
        top: likelyScroller.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, []);

  useEffect(() => {
    if (!isCreateTaskOpen || hasAutoScrolledToInlineCreateRef.current) {
      return;
    }

    hasAutoScrolledToInlineCreateRef.current = true;

    tableRootRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

    let rafOne = 0;
    let rafTwo = 0;
    let timeoutOne = 0;
    let timeoutTwo = 0;

    rafOne = window.requestAnimationFrame(() => {
      scrollToInlineCreateForm();
      rafTwo = window.requestAnimationFrame(() => {
        scrollToInlineCreateForm();
      });
    });
    timeoutOne = window.setTimeout(() => {
      scrollToInlineCreateForm();
    }, 240);
    timeoutTwo = window.setTimeout(() => {
      scrollToInlineCreateForm();
    }, 520);

    return () => {
      if (rafOne) window.cancelAnimationFrame(rafOne);
      if (rafTwo) window.cancelAnimationFrame(rafTwo);
      if (timeoutOne) window.clearTimeout(timeoutOne);
      if (timeoutTwo) window.clearTimeout(timeoutTwo);
    };
  }, [isCreateTaskOpen, scrollToInlineCreateForm]);

  const handleUpdateTask = useCallback(
    async ({
      taskId,
      payload,
    }: {
      taskId: string;
      payload: UpdateTaskPayload;
    }) => {
      await updateTask({ taskId, payload }).unwrap();
    },
    [updateTask],
  );

  const handleDeleteTask = useCallback(
    async (taskId: string) => {
      try {
        await deleteTask({ taskId }).unwrap();
        notify({ type: 'success', message: 'Task deleted.' });
      } catch (error) {
        notify({ type: 'error', message: getErrorMessage(error) });
      }
    },
    [deleteTask],
  );

  const handleCloseInlineCreate = useCallback(() => {
    setInlineCreateForm(getInitialInlineCreateForm());
    setInlineCreateErrors({});
    onCloseCreateTask();
  }, [onCloseCreateTask]);

  const handleCreateInlineTask = useCallback(async () => {
    const validationErrors = validateInlineCreateForm(inlineCreateForm);
    if (Object.keys(validationErrors).length > 0) {
      setInlineCreateErrors(validationErrors);
      return;
    }

    const dueDateIso = toIsoDateString(inlineCreateForm.dueDate);
    if (!dueDateIso) {
      setInlineCreateErrors((currentErrors) => ({
        ...currentErrors,
        due_date: 'Use a valid date (YYYY-MM-DD).',
      }));
      return;
    }

    const payload: CreateTaskPayload = {
      title: inlineCreateForm.title.trim(),
      description: inlineCreateForm.description.trim() || null,
      status: inlineCreateForm.status,
      due_date: dueDateIso,
      company_contact_id:
        inlineCreateForm.clientMode === 'client'
          ? (inlineCreateForm.company?.id ?? null)
          : null,
      person_contact_id:
        inlineCreateForm.clientMode === 'client'
          ? (inlineCreateForm.person?.id ?? null)
          : null,
      task_assignee_payloads: [
        {
          user_id: inlineCreateForm.assigneeId,
          service_type_id: inlineCreateForm.serviceTypeId || null,
          level_of_effort: inlineCreateForm.levelOfEffort ?? 0,
        },
      ],
    };

    try {
      setInlineCreateErrors({});
      await createTask(payload).unwrap();
      notify({ type: 'success', message: 'Quick task created successfully.' });
      handleCloseInlineCreate();
      void refetch();
    } catch (error) {
      const backendErrors = (error as any)?.data?.errors;
      const mappedErrors = backendErrorsToFieldErrors(backendErrors, {
        mapPath: mapBackendTaskFieldPath,
      });

      if (Object.keys(mappedErrors).length > 0) {
        setInlineCreateErrors(mappedErrors as InlineCreateErrors);
      }

      notify({ type: 'error', message: getErrorMessage(error) });
    }
  }, [createTask, handleCloseInlineCreate, inlineCreateForm, refetch]);

  const columns = useMemo(
    () => [
      {
        id: 'task_name',
        header: 'Task name',
        accessor: (row: QuickTaskRow) =>
          isInlineCreateRow(row) ? inlineCreateForm.title : (row.title ?? ''),
        sortable: true,
        minWidth: 120,
        cell: (row: QuickTaskRow) => {
          if (isInlineCreateRow(row)) {
            return (
              <div className={styles.quickTasksTable__taskName}>
                <SmartInput
                  type="text"
                  name="quick-task-inline-title"
                  value={inlineCreateForm.title}
                  error={inlineCreateErrors.title}
                  onChange={(nextValue) => {
                    setInlineCreateForm((current) => ({
                      ...current,
                      title: nextValue,
                    }));
                    clearInlineCreateError('title');
                  }}
                />
                <TaskStatusSelect
                  value={inlineCreateForm.status}
                  label=""
                  size="small"
                  name="quick-task-inline-status"
                  onChange={(nextValue) =>
                    setInlineCreateForm((current) => ({
                      ...current,
                      status: nextValue,
                    }))
                  }
                />
              </div>
            );
          }

          return (
            <div className={styles.quickTasksTable__taskName}>
              <EditableTaskTitle
                taskId={row.id}
                value={row.title}
                canEdit={canEditTasks}
                className={styles.quickTasksTable__taskTitle}
                onUpdated={() => {
                  void refetch();
                }}
              />
              <UpdateTaskStatus taskId={row.id} value={row.status} />
            </div>
          );
        },
      },
      {
        id: 'description',
        header: 'Description',
        accessor: (row: QuickTaskRow) =>
          isInlineCreateRow(row)
            ? inlineCreateForm.description
            : (row.description ?? ''),
        sortable: true,
        minWidth: 120,
        cell: (row: QuickTaskRow) => (
          <span className={styles.quickTasksTable__description}>
            {isInlineCreateRow(row)
              ? 'Add description below'
              : truncateWithEllipsis(row.description ?? '', 40) ||
                'Add description below'}
          </span>
        ),
      },
      {
        id: 'context',
        header: 'Context',
        accessor: (row: QuickTaskRow) => {
          if (isInlineCreateRow(row)) {
            return inlineCreateForm.company?.name ?? '';
          }

          return row.client?.company?.name ?? '';
        },
        sortable: true,
        minWidth: 90,
        cell: (row: QuickTaskRow) => {
          if (isInlineCreateRow(row)) {
            if (inlineCreateForm.clientMode === 'internal') {
              return (
                <span className={styles.quickTasksTable__contextInternal}>
                  <Badge label="Internal" tone="default" size="sm" />
                </span>
              );
            }

            return inlineCreateForm.company?.name ? (
              <strong>{inlineCreateForm.company.name}</strong>
            ) : (
              <span className={styles.quickTasksTable__description}>
                Client
              </span>
            );
          }

          const companyName = row.client?.company?.name;
          if (companyName) {
            return <strong>{companyName}</strong>;
          }

          return (
            <span className={styles.quickTasksTable__contextInternal}>
              <Badge label="Internal" tone="default" size="sm" />
            </span>
          );
        },
      },
      {
        id: 'assignee',
        header: 'Assignee',
        accessor: (row: QuickTaskRow) =>
          isInlineCreateRow(row)
            ? inlineCreateForm.assigneeId
            : getAssigneeName(row),
        sortable: true,
        minWidth: 100,
        cell: (row: QuickTaskRow) => {
          if (isInlineCreateRow(row)) {
            return (
              <div className={styles.quickTasksTable__assigneeCell}>
                <AssigneeSelect
                  value={inlineCreateForm.assigneeId}
                  label=""
                  showPreview={false}
                  error={inlineCreateErrors.assignee_id}
                  onChange={(nextValue) => {
                    setInlineCreateForm((current) => ({
                      ...current,
                      assigneeId: nextValue,
                    }));
                    clearInlineCreateError('assignee_id');
                  }}
                />
              </div>
            );
          }

          const assignee = getPrimaryAssignee(row)?.user;
          if (!assignee) {
            return 'N/A';
          }

          return (
            <span className={styles.quickTasksTable__assignee}>
              <WorkspaceMemberAvatar
                size="small"
                user={assignee}
                closeOnTriggerMouseLeave
              />
            </span>
          );
        },
      },
      {
        id: 'service_type',
        header: 'Service type',
        accessor: (row: QuickTaskRow) =>
          isInlineCreateRow(row)
            ? inlineCreateForm.serviceTypeId
            : (getPrimaryAssignee(row)?.service_type?.label ??
              serviceTypeLabelMap.get(getAssigneeServiceTypeId(row)) ??
              ''),
        sortable: true,
        minWidth: 100,
        cell: (row: QuickTaskRow) => {
          if (isInlineCreateRow(row)) {
            return (
              <div className={styles.quickTasksTable__serviceTypeCell}>
                <ServiceTypeSelect
                  value={inlineCreateForm.serviceTypeId}
                  includeAllOption={false}
                  size="small"
                  label=""
                  error={inlineCreateErrors.service_type_id}
                  minWidth={0}
                  onChange={(nextValue) => {
                    setInlineCreateForm((current) => ({
                      ...current,
                      serviceTypeId: nextValue,
                    }));
                    clearInlineCreateError('service_type_id');
                  }}
                  name="quick-task-inline-service-type"
                />
              </div>
            );
          }

          return (
            <span className={styles.quickTasksTable__serviceType}>
              {getPrimaryAssignee(row)?.service_type?.label ??
                serviceTypeLabelMap.get(getAssigneeServiceTypeId(row)) ??
                'N/A'}
            </span>
          );
        },
      },
      {
        id: 'due_date',
        header: 'Due date',
        accessor: (row: QuickTaskRow) =>
          isInlineCreateRow(row)
            ? (inlineCreateForm.dueDate?.toISOString() ?? '')
            : (row.due_date ?? ''),
        sortable: true,
        minWidth: 80,
        cell: (row: QuickTaskRow) => {
          if (isInlineCreateRow(row)) {
            return (
              <div className={styles.quickTasksTable__dueDateCell}>
                <DatePicker
                  name="quick-task-inline-due-date"
                  label=""
                  value={inlineCreateForm.dueDate}
                  error={inlineCreateErrors.due_date}
                  onChange={(nextValue) => {
                    setInlineCreateForm((current) => ({
                      ...current,
                      dueDate: nextValue,
                    }));
                    clearInlineCreateError('due_date');
                  }}
                />
              </div>
            );
          }

          return (
            <UpdateTaskDueDate
              taskId={row.id}
              value={row.due_date}
              onUpdated={() => {
                void refetch();
              }}
            />
          );
        },
      },
      {
        id: 'loe_timeline',
        header: 'LOE / Timeline',
        accessor: (row: QuickTaskRow) =>
          isInlineCreateRow(row)
            ? (inlineCreateForm.levelOfEffort ?? 0)
            : (getPrimaryAssignee(row)?.level_of_effort ?? 0),
        sortable: true,
        minWidth: 90,
        cell: (row: QuickTaskRow) =>
          isInlineCreateRow(row) ? (
            <SmartInput
              type="number"
              name="quick-task-inline-level-of-effort"
              value={inlineCreateForm.levelOfEffort}
              min={0}
              step={0.1}
              error={inlineCreateErrors.level_of_effort}
              onChange={(nextValue) => {
                setInlineCreateForm((current) => ({
                  ...current,
                  levelOfEffort: nextValue,
                }));
                clearInlineCreateError('level_of_effort');
              }}
            />
          ) : (
            <div className={styles.quickTasksTable__meterStack}>
              <Meter
                percentage={getLoePercentage(row)}
                label={`LOE ${formatHours(getPrimaryAssignee(row)?.tracked_hours)} / ${formatHours(getPrimaryAssignee(row)?.level_of_effort)}h`}
                tooltipContent={
                  <span>
                    {`Tracked ${formatHours(getPrimaryAssignee(row)?.tracked_hours)}h of ${formatHours(getPrimaryAssignee(row)?.level_of_effort)}h`}
                  </span>
                }
                ariaLabel="LOE meter"
              />

              <Meter
                percentage={row.due_date_time_percentage ?? 0}
                label={
                  <span
                    className={
                      row.days_to_due !== null && row.days_to_due < 0
                        ? styles.quickTasksTable__timelineAlert
                        : undefined
                    }
                  >
                    {getTimelineStatusLabel(row.due_date, row.days_to_due)}
                  </span>
                }
                tooltipContent={
                  <span>
                    {`${formatDueDateLabel(row.due_date)} • ${getTimelineStatusLabel(row.due_date, row.days_to_due)}`}
                  </span>
                }
                ariaLabel="Timeline meter"
              />
            </div>
          ),
      },
      {
        id: 'actions',
        header: '',
        accessor: (row: QuickTaskRow) => (isInlineCreateRow(row) ? '' : row.id),
        sortable: false,
        minWidth: 50,
        cell: (row: QuickTaskRow) => {
          if (isInlineCreateRow(row)) {
            return '';
          }

          return (
            <div className={styles.quickTasksTable__cellAction}>
              <OptionsMenu
                ariaLabel="Quick task actions"
                actions={[
                  {
                    key: 'delete',
                    label: 'Delete',
                    tone: 'danger',
                    loading: isDeletingTask,
                    onSelect: () => {
                      void handleDeleteTask(row.id);
                    },
                  },
                ]}
              />
            </div>
          );
        },
      },
    ],
    [
      clearInlineCreateError,
      canEditTasks,
      handleDeleteTask,
      inlineCreateErrors.assignee_id,
      inlineCreateErrors.due_date,
      inlineCreateErrors.level_of_effort,
      inlineCreateErrors.service_type_id,
      inlineCreateErrors.title,
      inlineCreateForm.assigneeId,
      inlineCreateForm.clientMode,
      inlineCreateForm.company,
      inlineCreateForm.description,
      inlineCreateForm.dueDate,
      inlineCreateForm.levelOfEffort,
      inlineCreateForm.serviceTypeId,
      inlineCreateForm.status,
      inlineCreateForm.title,
      isDeletingTask,
      refetch,
      serviceTypeLabelMap,
    ],
  );

  const inlineCreateSubForm = (
    <QuickTaskInlineCreateForm
      clientMode={inlineCreateForm.clientMode}
      description={inlineCreateForm.description}
      company={inlineCreateForm.company}
      person={inlineCreateForm.person}
      errors={inlineCreateErrors}
      isCreating={isCreatingTask}
      onClientModeChange={(nextMode) => {
        setInlineCreateForm((current) => ({
          ...current,
          clientMode: nextMode,
          company: nextMode === 'internal' ? null : current.company,
          person: nextMode === 'internal' ? null : current.person,
        }));
        clearInlineCreateError('company_id');
        clearInlineCreateError('person_id');
      }}
      onDescriptionChange={(nextDescription) => {
        setInlineCreateForm((current) => ({
          ...current,
          description: nextDescription,
        }));
        clearInlineCreateError('description');
      }}
      onCompanyChange={(nextCompany, nextPerson) => {
        setInlineCreateForm((current) => ({
          ...current,
          company: nextCompany,
          person: nextPerson,
        }));
        clearInlineCreateError('company_id');
        clearInlineCreateError('person_id');
      }}
      onPersonChange={(nextPerson, nextCompany) => {
        setInlineCreateForm((current) => ({
          ...current,
          company: nextCompany,
          person: nextPerson,
        }));
        clearInlineCreateError('company_id');
        clearInlineCreateError('person_id');
      }}
      onCancel={handleCloseInlineCreate}
      onCreate={() => {
        void handleCreateInlineTask();
      }}
    />
  );

  const showEmptyState = taskRows.length === 0 && !isLoading;
  const showStandaloneCreate = showEmptyState && isCreateTaskOpen;

  return (
    <div className={styles.quickTasksTable} ref={tableRootRef}>
      {showEmptyState && !isCreateTaskOpen ? (
        <EmptyState
          title="No quick tasks yet."
          description="Create a quick task to get started."
        />
      ) : null}

      {showStandaloneCreate ? (
        <div className={styles.quickTasksTable__standaloneCreate}>
          <div className={styles.quickTasksTable__standaloneCreateRow}>
            <div className={styles.quickTasksTable__taskName}>
              <SmartInput
                type="text"
                name="quick-task-inline-title"
                value={inlineCreateForm.title}
                error={inlineCreateErrors.title}
                onChange={(nextValue) => {
                  setInlineCreateForm((current) => ({
                    ...current,
                    title: nextValue,
                  }));
                  clearInlineCreateError('title');
                }}
              />
              <TaskStatusSelect
                value={inlineCreateForm.status}
                label=""
                size="small"
                name="quick-task-inline-status"
                onChange={(nextValue) =>
                  setInlineCreateForm((current) => ({
                    ...current,
                    status: nextValue,
                  }))
                }
              />
            </div>

            <span className={styles.quickTasksTable__description}>
              Add description below
            </span>

            <div>
              {inlineCreateForm.clientMode === 'internal' ? (
                <span className={styles.quickTasksTable__contextInternal}>
                  <Badge label="Internal" tone="default" size="sm" />
                </span>
              ) : inlineCreateForm.company?.name ? (
                <strong>{inlineCreateForm.company.name}</strong>
              ) : (
                <span className={styles.quickTasksTable__description}>
                  Client
                </span>
              )}
            </div>

            <div className={styles.quickTasksTable__assigneeCell}>
              <AssigneeSelect
                value={inlineCreateForm.assigneeId}
                label=""
                showPreview={false}
                error={inlineCreateErrors.assignee_id}
                onChange={(nextValue) => {
                  setInlineCreateForm((current) => ({
                    ...current,
                    assigneeId: nextValue,
                  }));
                  clearInlineCreateError('assignee_id');
                }}
              />
            </div>

            <div className={styles.quickTasksTable__serviceTypeCell}>
              <ServiceTypeSelect
                value={inlineCreateForm.serviceTypeId}
                includeAllOption={false}
                size="small"
                label=""
                error={inlineCreateErrors.service_type_id}
                minWidth={0}
                onChange={(nextValue) => {
                  setInlineCreateForm((current) => ({
                    ...current,
                    serviceTypeId: nextValue,
                  }));
                  clearInlineCreateError('service_type_id');
                }}
                name="quick-task-inline-service-type"
              />
            </div>

            <div className={styles.quickTasksTable__dueDateCell}>
              <DatePicker
                name="quick-task-inline-due-date"
                label=""
                value={inlineCreateForm.dueDate}
                error={inlineCreateErrors.due_date}
                onChange={(nextValue) => {
                  setInlineCreateForm((current) => ({
                    ...current,
                    dueDate: nextValue,
                  }));
                  clearInlineCreateError('due_date');
                }}
              />
            </div>

            <SmartInput
              type="number"
              name="quick-task-inline-level-of-effort"
              value={inlineCreateForm.levelOfEffort}
              min={0}
              step={0.1}
              error={inlineCreateErrors.level_of_effort}
              onChange={(nextValue) => {
                setInlineCreateForm((current) => ({
                  ...current,
                  levelOfEffort: nextValue,
                }));
                clearInlineCreateError('level_of_effort');
              }}
            />
          </div>

          {inlineCreateSubForm}
        </div>
      ) : null}

      {!showEmptyState ? (
        <InfiniteTable<QuickTaskRow>
          tableId="quick-tasks-table"
          columns={columns}
          rows={rows}
          getRowSortPosition={(row) =>
            isInlineCreateRow(row) ? 'first' : 'normal'
          }
          virtualizedHeight="auto"
          rowHeight={100}
          expandedRowIds={expandedRowIds}
          onExpandedRowIdsChange={(nextRowIds) => {
            if (
              isCreateTaskOpen &&
              !nextRowIds.includes(INLINE_CREATE_ROW_ID)
            ) {
              setExpandedRowIds([...nextRowIds, INLINE_CREATE_ROW_ID]);
              return;
            }

            setExpandedRowIds(nextRowIds);
          }}
          onLoadMore={() => {
            // * Quick Tasks table currently loads the first page only.
          }}
          hasMore={false}
          isLoading={isLoading}
          skeletonRowCount={8}
          emptyMessage="No quick tasks found."
          getRowId={(row: QuickTaskRow) => row.id}
          renderRowSubComponent={(row: QuickTaskRow) =>
            isInlineCreateRow(row) ? (
              inlineCreateSubForm
            ) : (
              <QuickTaskSubRowForm
                key={`${row.id}:${row.updated_at}`}
                task={row}
                onUpdateTask={handleUpdateTask}
              />
            )
          }
          isRowCollapsible={() => true}
        />
      ) : null}
    </div>
  );
}
